import React, { ReactElement, ReactNode } from 'react';
import { NextPage } from 'next';

import { CacheProvider, EmotionCache } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next';

import NavigationDirectionContext from '@/context/navigation-direction-context';
import theme from '@/styles/theme/theme';
import { createEmotionCacheUtils } from '@/utils/create-emotion-cache.utils';

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = MyAppProps & {
  Component: NextPageWithLayout;
};

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCacheUtils();

const App = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps
}: AppPropsWithLayout) => {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <meta name="description" content="Generated by create next app" />
        <meta name="canopy-care" content="app-id=1368449357" />
        <link rel="icon" href="/images/favicons/favicon-32x32.png" />
        <link rel="icon" href="/images/favicons/favicon.svg" type="image/svg+xml" />
        <link
          rel="icon"
          href="/images/favicons/favicon-light.svg"
          type="image/svg+xml"
          media="(prefers-color-scheme: dark)"
        />
        <link
          rel="icon"
          href="/images/favicons/favicon-dark.svg"
          type="image/svg+xml"
          media="(prefers-color-scheme: light)"
        />
      </Head>

      <ThemeProvider theme={theme}>
        <CssBaseline />

        <NavigationDirectionContext>
          {getLayout(<Component {...pageProps} />)}
        </NavigationDirectionContext>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default appWithTranslation(App);
